import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';

import DeviceList from 'src/component/v2/DeviceList/DeviceList';
import DeviceFilterChips from 'src/component/v2/DeviceList/DeviceFilterChips';
import DeviceListSummary from 'src/component/v2/DeviceList/DeviceListSummary';
import DeviceNav from 'src/component/v2/DeviceNav/DeviceNav';
import useDeviceNavSelection from 'src/component/v2/DeviceNav/useDeviceNavSelection';
import { columnsForSelection, toDeviceListScope } from 'src/component/v2/DeviceNav/deviceNavState';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import { isWhitelabel } from 'src/utils/utils';

/* Reserves space for a part of the design that isn't built yet. Labels are for developers only. */
const Placeholder = ({ label, className }: { label: string; className?: string }) => (
  <div
    aria-hidden='true'
    className={cn(
      'ds-flex ds-items-center ds-justify-center ds-rounded ds-border ds-border-dashed ds-border-border',
      'ds-text-xs ds-text-muted-foreground',
      className
    )}
  >
    {label}
  </div>
);

export default function GroupedDevicesScreen () {
  const { t } = useTranslation();
  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';
  const [selection, setSelection] = useDeviceNavSelection();
  const scope = useMemo(() => toDeviceListScope(selection), [selection]);
  const columns = columnsForSelection(selection);

  return (
    <div className='ds-flex ds-h-full ds-flex-col ds-bg-background'>
      <Helmet>
        <title>{title} - {t('deviceList.title')}</title>
      </Helmet>

      <h1 className='ds-sr-only'>{t('deviceList.title')}</h1>

      <div className='ds-flex ds-items-center ds-gap-4 ds-border-b ds-border-border ds-px-4 ds-py-2'>
        <Placeholder label='Area filter' className='ds-h-8 ds-w-[320px]' />
        <div className='ds-ml-auto ds-flex ds-gap-4'>
          <Placeholder label='Location' className='ds-h-8 ds-w-[200px]' />
          <Placeholder label='Commodity' className='ds-h-8 ds-w-[200px]' />
          <Placeholder label='Clear all' className='ds-h-8 ds-w-[80px]' />
        </div>
      </div>

      <div className='ds-flex ds-min-h-0 ds-flex-1 ds-gap-4 ds-px-4 ds-pt-4'>
        <DeviceNav selection={selection} onSelect={setSelection} className='ds-mb-4' />

        <div className='ds-flex ds-min-w-0 ds-flex-1 ds-flex-col ds-gap-8'>
          <div className='ds-flex ds-items-center ds-gap-2'>
            <Placeholder label='Search devices' className='ds-h-8 ds-w-[280px]' />
            <Placeholder label='Actions' className='ds-ml-auto ds-h-8 ds-w-[160px]' />
          </div>
          <div className='ds-flex ds-flex-col ds-gap-1'>
            <DeviceFilterChips />
            <DeviceListSummary />
          </div>

          <DeviceList
            scope={scope}
            columns={columns}
            onViewAllDevices={() => setSelection({ tab: selection.tab })}
            className='ds-min-h-0 ds-flex-1'
          />
        </div>
      </div>
    </div>
  );
}
